import { Extension } from '@tiptap/core'
import {
  parseStyleString,
  responsiveVarName,
  stringifyStyleObject
} from '@/plugins/tiptap-v3/functionality/responsive/responsive'

const getBreakpoint = editor => editor?.storage?.responsiveEngine?.breakpoint || 'lg'

const getResponsiveConfig = options => {
  if (!options?.responsive || !options.responsive.enabled) return null

  return {
    prefix: options.responsive.prefix || 'vp',
    cssPropLetterSpacing: options.responsive.cssPropLetterSpacing || 'letter-spacing'
  }
}

export const LetterSpacing = Extension.create({
  name: 'letterSpacing',

  addOptions () {
    return {
      responsive: {
        enabled: true,
        prefix: 'vp',
        cssPropLetterSpacing: 'letter-spacing'
      }
    }
  },

  addGlobalAttributes () {
    const cfg = getResponsiveConfig(this.options)

    return [
      {
        types: ['textStyle'],
        attributes: {
          letterSpacing: {
            default: null,
            parseHTML: element => element.style.letterSpacing || null,
            renderHTML: attributes => {
              if (!attributes.letterSpacing) {
                return {}
              }

              // In responsive mode we keep letter-spacing as CSS vars on `style`.
              // Avoid emitting a fixed inline letter-spacing from this attribute.
              if (cfg) {
                return {}
              }

              return {
                style: `letter-spacing: ${attributes.letterSpacing}`
              }
            }
          }
        }
      }
    ]
  },

  addCommands () {
    return {
      setLetterSpacing:
        (space, opts) =>
          ({ editor, chain }) => {
            const cfg = getResponsiveConfig(this.options)

            // Non-responsive behavior (backwards compatible)
            if (!cfg) {
              return chain()
                .setMark('textStyle', { letterSpacing: space })
                .run()
            }

            const bp = opts?.breakpoint || getBreakpoint(editor)
            const lsVar = responsiveVarName(cfg.cssPropLetterSpacing, bp, cfg.prefix)

            const attrs = editor.getAttributes('textStyle') || {}
            const styleObj = parseStyleString(attrs.style || '')
            // Legacy compatibility: remove fixed letter-spacing so responsive vars are authoritative.
            delete styleObj['letter-spacing']
            styleObj[lsVar] = space

            return chain()
              .setMark('textStyle', {
                ...attrs,
                style: stringifyStyleObject(styleObj),
                letterSpacing: null
              })
              .run()
          },

      unsetLetterSpacing:
        opts =>
          ({ editor, chain }) => {
            const cfg = getResponsiveConfig(this.options)

            // Non-responsive behavior
            if (!cfg) {
              return chain()
                .setMark('textStyle', { letterSpacing: null })
                .removeEmptyTextStyle?.()
                .run()
            }

            const bp = opts?.breakpoint || getBreakpoint(editor)
            const lsVar = responsiveVarName(cfg.cssPropLetterSpacing, bp, cfg.prefix)

            const attrs = editor.getAttributes('textStyle') || {}
            const styleObj = parseStyleString(attrs.style || '')
            delete styleObj['letter-spacing']
            delete styleObj[lsVar]

            return chain()
              .setMark('textStyle', {
                ...attrs,
                style: stringifyStyleObject(styleObj),
                letterSpacing: null
              })
              .removeEmptyTextStyle?.()
              .run()
          }
    }
  }
})
